import { useState, useEffect, useRef, useMemo } from 'react';
import { sandhillsRequest, repositionCookieBanner, getURLParameter, insertURLParameter, removeURLParameter, setScrollTop, setIOSScrollLock, removeIOSScrollLock, gtmPushEvent, logSAMError, syncCanonicalUrl } from '@Components/Utilities/SandhillsUtility';
import { getUserPosition } from '@Components/Utilities/Geolocation';
import { contactType, inquiryType, gtmEventType } from '@Components/Utilities/SandhillsConstants';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import translator from '@Components/Utilities/Translator';
import FacetedSearchMobileMenu from '@Components/Shared/FacetedSearch/FacetedSearchMobileMenu';
import Paging from '@Components/MuiWrappers/Paging';
import SimpleHomeBreadcrumb from '@Components/Shared/SimpleHomeBreadcrumb';
import TranslationText from '@Components/Utilities/TranslationText';
import SortDropdown from '@Components/Shared/SortDropdown';
import FacetedSearch from '@Components/Shared/FacetedSearch/FacetedSearch';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { togglePushNotifications } from '@Components/Shared/PushNotificationButton';
import { toggleScrollToTop } from '@Components/Shared/ScrollToTop';
import ready from 'document-ready';
import AuctioneerDirectoryListing from '@Components/Auctioneers/AuctioneerDirectoryListing';
import ShowClosestFirstToggle from '@Components/Shared/ShowClosestFirstToggle';
import debounce from 'lodash.debounce';
const FacetedSearchSelectedFacet = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearchSelectedFacet'));
const EmailModal = loadable(() => import('@Components/Shared/Modals/EmailModal'), true);
const DistanceLocationModal = loadable(() => import('@Components/Shared/Modals/DistanceLocationModal'), true);
const SEOLinks = loadable(() => import('@Components/Listings/SEOLinks'));

const AuctioneerListView = (props) => {
    const currentUser = useRecoilValue(atoms.currentUser);
    const routes = useRecoilValue(atoms.routes);
    const ref = useRef([]);
    const constants = {
        Sort: 'Sort',
        UseCurrentLocation: 'UseCurrentLocation'  
    };
    const [searchData, setSearchData] = useState({
        SearchBaseURL: props.AuctioneerDirectoryURL,
        Auctioneers: props.Auctioneers,
        SearchFields: props.SearchFields,
        ListingsCount: props.PaginationModel.TotalListingsCount,
        PageHeader: props.PageHeader,
        ListingCountHeader: props.ListingCountHeader,
        CurrentPage: props.PaginationModel.CurrentPage ? props.PaginationModel.CurrentPage : 1,
        DisplayShowClosestFirstByConsent: props.DisplayShowClosestFirstByConsent,
        TotalPages: props.PaginationModel.TotalPages,
        InternalLinkingModel: props.InternalLinkingModel,
        SelectedSortValue: props.SelectedSortValue,
        IsDefaultSearch: props.IsDefaultSearch,
        LocationString: props.LocationString,
    });
    const [criteriaData, setCriteriaData] = useState({
        PageHeader: props.PageHeader,
        PendingSearchCriteria: [],
        SelectedFacets: props.SearchFields,
        SelectedSortValue: props.SelectedSortValue,
        SearchBaseURL: props.AuctioneerDirectoryURL ,
        SearchPageURL: props.auctioneerDirectorySearchURL ,
        IsDefaultSearch: props.IsDefaultSearch ,
        SAMModel: props.SAMModel,
    });
    const [loadImagesForPrint, setLoadImagesForPrint] = useState(false);
    const windowSize = useWindowSize();
    const [isMounted, setIsMounted] = useState(false);
    const [useCurrentLocation, setUseCurrentLocation] = useState(false);
    const [auctioneerDirectorySearchURL, setAuctioneerDirectorySearchURL] = useState(props.AuctioneerDirectorySearchURL);
    const [showSearchWindow, setShowSearchWindow] = useState(false);
    const [mobileSearch, setMobileSearch] = useState(false);
    const [isPopState, setIsPopState] = useState(false);
    const [searchInProgress, setSearchInProgress] = useState(false);
    const [outstandingChanges, setOutstandingChanges] = useState([]);
    const [showDistanceModal, setShowDistanceModal] = useState(false);
    const [showDistanceSortError, setShowDistanceSortError] = useState(false);
    const [shouldRunSearch, setShouldRunSearch] = useState(false);
    const [samModel, setSamModel] = useState(props.SAMModel);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedInquiry, setSelectedInquiry] = useState('');
    const [selectedContactType, setSelectedContactType] = useState(contactType.Retail);
    const [showMobileSort, setShowMobileSort] = useState(false);
    const [returnFocusRef, setReturnFocusRef] = useState({});
    const [showShareButtons, setShowShareButtons] = useState(false);

    useEffect(function runFacetedSearch() {
        if (isMounted && !showDistanceModal) {
            runSearch(false);
        }
    }, [showDistanceModal, searchData.SelectedSortValue, shouldRunSearch]);

    useEffect(function runPageSearch() {
        if (isMounted) {
            runSearch(true);
        }
    }, [searchData.CurrentPage]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, true);
        const cookieBannerResize = () => {
            repositionCookieBanner('mobile-option-bar', 786);
        };

        ready(() => {
            cookieBannerResize();
            window.addEventListener('resize', cookieBannerResize);
            if (window.outerWidth > 785) {
                document.getElementsByTagName('body')?.[0]?.classList.remove('fixed');
            }
            if (props.OpenSCFModalOnLoad) {
                onDistanceLocationModalToggle();
            }

            setIsMounted(true);
        });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', cookieBannerResize);
        };
    }, []);

    const buildUrl = (keepPage, pendingChanges, searchField) => {
        let searchUrl = auctioneerDirectorySearchURL;
        if (pendingChanges) {
            pendingChanges.forEach(field => {
                searchUrl = removeURLParameter(searchUrl, field.fieldName);
                if (field.value) {
                    searchUrl = insertURLParameter(searchUrl, field.fieldName, field.value);
                }
            });
        }

        if (searchData.SelectedSortValue == 6) {
            searchUrl = insertURLParameter(searchUrl, constants.Sort, searchData.SelectedSortValue);
        } else {
            searchUrl = removeURLParameter(searchUrl, constants.Sort);
        }

        if (searchField) {
            searchUrl = insertURLParameter(searchUrl, 'SearchProperty', searchField);
        }

        searchUrl = removeURLParameter(searchUrl, 'Page');
        if (keepPage === true) {
            searchUrl = insertURLParameter(searchUrl, 'Page', searchData.CurrentPage);
        }

        searchUrl = removeURLParameter(searchUrl, constants.UseCurrentLocation);
        if (useCurrentLocation) {
            searchUrl = insertURLParameter(searchUrl, constants.UseCurrentLocation, useCurrentLocation)
        }

        return searchUrl;
    };

    const updatePendingChangesForSearch = (pendingChanges, latestFieldValue) => {
        let newField = true;
        for (var i = 0; i < pendingChanges.length; i++) {
            if (latestFieldValue.fieldName == pendingChanges[i].fieldName) {
                pendingChanges[i].value = latestFieldValue.value;
                newField = false;
                break;
            }
        }

        if (newField) {
            pendingChanges.push(latestFieldValue);
        }

        let fields = [...searchData.SearchFields];
        for (var j = 0; j < fields.length; j++) {
            let field = fields[j];
            if (latestFieldValue.fieldName == field.FieldName && field.Type == 'Range') {
                // Update the range field in state so it doesn't do a weird re-render with the old value:
                let splitValue = latestFieldValue.value.split('*');
                field.SelectedLowerBound = splitValue[0];
                field.SelectedUpperBound = splitValue[1];
                setSearchData(previousState => {
                    return { ...previousState, SearchFields: fields };
                });
                break;
            }
        }

        return pendingChanges;
    };

    const runSearch = (keepPage, latestFieldValue) => {
        let pendingChanges = [...outstandingChanges];
        if (latestFieldValue) {
            if (Array.isArray(latestFieldValue)) {
                for (var i = 0; i < latestFieldValue.length; i++) {
                    pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue[i]);
                }
            } else {
                pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue);
            }
        }

        let searchField = undefined;
        if (event && event.target) {
            searchField = event.target.name;
        }

        if (searchField == 'InventoryType') {
            let inventoryType = searchData.SearchFields.filter(field => field.FieldName == 'InventoryType')[0];
            if (inventoryType.Options.filter(option => option.Checked).length == 1 && inventoryType.Options.filter(option => option.Checked)[0].Value == 'Parts') {
                let stateFacet = searchData.SearchFields.filter(field => field.FieldName == 'State')[0];
                let countryFacet = searchData.SearchFields.filter(field => field.FieldName == 'Country')[0];
                if (stateFacet) {
                    stateFacet.Options.map(option => option.Checked = false);
                }

                if (countryFacet) {
                    countryFacet.Options.map(option => option.Checked = false);
                }
            }
        }

        if (windowSize.width <= 785) {
            setScrollTop(0);
        }

        performSearch(buildUrl(keepPage, pendingChanges, searchField));
    };

    const performSearch = (newSearchURL) => {
        setSearchInProgress(true);
        setOutstandingChanges([]);
        setAuctioneerDirectorySearchURL(newSearchURL);
        sandhillsRequest({
            url: newSearchURL,
            method: 'post',
        }).then((data) => {
            handleResponse(data);
        });
    };

    const handleResponse = (result) => {
        setSearchData({
            SearchBaseURL: result.AuctioneerDirectoryURL,
            Auctioneers: result.Auctioneers,
            SearchFields: result.SearchFields,
            ListingsCount: result.PaginationModel.TotalListingsCountFormatted,
            PageHeader: result.PageHeader,
            ListingCountHeader: result.ListingCountHeader,
            CurrentPage: result.PaginationModel.CurrentPage,
            DisplayShowClosestFirstByConsent: result.DisplayShowClosestFirstByConsent,
            TotalPages: result.PaginationModel.TotalPages,
            InternalLinkingModel: result.InternalLinkingModel,
            SelectedSortValue: result.SelectedSortValue,
            IsDefaultSearch: result.IsDefaultSearch,
            CategoryInfo: result.CategoryInformation,
            LocationString: result.LocationString
        });

        setAuctioneerDirectorySearchURL(result.AuctioneerDirectorySearchURL);
        setSearchInProgress(false);
        document.title = result.PageTitle;
        window.history.pushState('', '', result.AuctioneerDirectoryURL);
        syncCanonicalUrl(result.AuctioneerDirectoryURL);
    };

    const toggleMobileSort = () => {
        togglePushNotifications(showMobileSort);
        toggleScrollToTop(showMobileSort);
        setShowMobileSort(!showMobileSort);
    };

    const handleScroll = () => {
        if (showMobileSort) {
            setShowMobileSort(false);
        }
    };

    const handlePageNumberChange = (page) => {
        setIsPopState(false);
        setSearchData(previousState => {
            return { ...previousState, CurrentPage: page };
        });
        setScrollTop(0);
    };

    const toggleSearchWindow = () => {
        if (windowSize.isMobile) {
            if (showSearchWindow) {
                removeIOSScrollLock(true);
            } else {
                setIOSScrollLock(true);
            }
        }
        let showWindow = !showSearchWindow;
        if (showWindow) {
            document.getElementsByTagName('body')?.[0]?.classList.add('fixed');
        } else {
            document.getElementsByTagName('body')?.[0]?.classList.remove('fixed');
        }

        setShowSearchWindow(showWindow);
        setMobileSearch(showWindow && window.innerWidth <= 785)
    };

    const clearAll = () => {
        setScrollTop(0);
        setSearchInProgress(true);
        setAuctioneerDirectorySearchURL(props.AuctioneerDirectorySearchURL.split('?')[0]);
        setSearchData(previousState => {
            return { ...previousState, SelectedSortValue: 1 };
        });

        sandhillsRequest({
            url: props.AuctioneerDirectorySearchURL.split('?')[0],
            method: 'post'
        }).then((data) => {
            handleResponse(data)
        });

        window.history.pushState('', '', searchData.SearchBaseURL.split('?')[0]);
        syncCanonicalUrl(searchData.SearchBaseURL.split('?')[0]);
    };

    const clearFacet = (fieldName, option) => {
        let searchUrl = buildUrl(false);
        //copy field name so we don't mutate 
        let queryStringParamName = (' ' + fieldName).slice(1);
        let selectedField = searchData.SearchFields.filter(f => f.FieldName == fieldName)[0];

        if (option && option.IsGroup && selectedField?.ParentNameForSearch) {
            queryStringParamName = selectedField.ParentNameForSearch;
        }

        if (option) {
            let qsValues = getURLParameter(searchUrl, queryStringParamName).split('|');
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);

            let filter = qsValues;
            let index = qsValues.indexOf(option.Value ? option.Value : option);
            if (index > -1) {
                filter.splice(index, 1);
            }

            if (filter.length > 0 && filter[0] !== "") {
                searchUrl = insertURLParameter(searchUrl, queryStringParamName, filter.join('|'));
            }
        } else {
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);
        }

        performSearch(searchUrl);
    };

    const clearFacetDebounced = useMemo(() => debounce(clearFacet, 300), [clearFacet]);

    const onDistanceLocationModalToggle = () => {
        let newShowDistanceModal = !showDistanceModal;
        setShowDistanceModal(newShowDistanceModal);
    };

    const onDistanceLocationModalUpdate = (location) => {
        setOutstandingChanges(previousState => {
            return [
                ...previousState
            ];
        });
        setUseCurrentLocation(location?.UseCurrentLocation ?? false);
        setSearchData(previousState => {
            return { ...previousState, SelectedSortValue: 6 };
        });
        setShouldRunSearch(!shouldRunSearch);
        setShowDistanceModal(false);
    };

    const onSortChange = (sortID) => {
        setUseCurrentLocation(false);
        if (sortID == 6 && searchData.SelectedSortValue != 6) {
            getUserPosition(true, null, routes.AjaxSetCurrentUserLocationCookie, routes.AjaxGetDecryptedCoordinatesFromCurrentLocationCookie).then((currentPosition) => {
                if (currentPosition && currentPosition.latitude && currentPosition.longitude) {
                    setOutstandingChanges(previousState => {
                        return [
                            ...previousState,
                        ];
                    });
                    setSearchData(previousState => {
                        return { ...previousState, SelectedSortValue: sortID };
                    });
                } else {
                    onDistanceLocationModalToggle();
                }
            }).catch(distanceSortError(true));
        } else {
            setSearchData(previousState => {
                return { ...previousState, SelectedSortValue: sortID };
            });
        }
    };

    const onEmailModalToggle = () => {
        if (returnFocusRef) {
            setTimeout(() => {
                returnFocusRef.focus();
            }, 150);
        }
        setShowEmailModal(showEmailModal => !showEmailModal);
    };

    const fireSendAMessageError = (inquiry, contact, data) => {

        const errorData = {
            CRMID: data.DealerCRMID,
            DealerGroupID: data.DealerGroupID,
            ListingBaseCatID: data.ListingBaseCatID,
            ListingCategoryID: data.ListingCategoryID,
            ListingID: data.ListingID,
            ListingManufacturer: data.ListingManufacturer,
            ListingModel: data.ListingModel,
            ListingSourceDomainUrl: data.ListingSourceDomainUrl,
            ListingURL: data.ListingURL,
            ModalTitle: data.ModalTitle,
            Subject: data.Subject
        }

        let msg = `Error on list page modal open: \r\nInquiry: ${inquiry} \r\nContact: ${contact} \r\nData: ${errorData ?? JSON.stringify(errorData)}`;
        logSAMError(routes.LogSendAMessageError, msg);
    }

    const openEmailModal = (inquiry, contact, modalData) => {
        if (inquiry != inquiryType.EmailThis && (!modalData?.DealerGroupID || modalData?.DealerGroupID == 0) && (!modalData?.ListingID || modalData?.ListingID == 0)) {
            fireSendAMessageError(inquiry, contact, modalData);
        }
        modalData = modalData || {};
        modalData.IsShowroom = props.IsShowroom;

        if (contact != contactType.Wholesale && contact != contactType.ExclusiveWholesale) {
            contact = contactType.Retail;
        }
        if (inquiry == inquiryType.EmailThis) {
            modalData.ModalTitle = translator.translate('sandhillssystem.share').toString();

            if (windowSize.isMobile && navigator.share) {
                navigator.share({
                    title: document.title,
                    url: window.location.href
                })
                    .then(() => gtmPushEvent(gtmEventType.MobileShare))
                    .catch(() => setShowShareButtons(true));
                return;
            } else {
                setShowShareButtons(true);
            }
        } else {
            setShowShareButtons(false);
        }

        setTimeout(() => {
            setReturnFocusRef(modalData.Ref || ref.current);
        }, 150);
        setSelectedInquiry(inquiry);
        setSelectedContactType(contact);
        setShowEmailModal(showEmailModal => !showEmailModal);
        setCriteriaData(previousState => {
            return {
                ...previousState, SAMModel: {
                    ...previousState.SAMModel,
                    ...modalData
                }
            };
        });
    };

    const onPrint = () => {
        gtmPushEvent(gtmEventType.printListDetail);
        setLoadImagesForPrint(true);
        window.print(); 
    };

    const distanceSortError = (showError) => {
        setShowDistanceSortError(showError);
        setShowDistanceModal(showError);
    };

    const buildDistanceSortProps = () => {
        return {
            OnUpdate: onDistanceLocationModalUpdate,
            LocationAutosuggestUrl: routes.LocationAutosuggestUrl,
            AjaxGetCoordinatesFromLocationStringUrl: routes.AjaxGetCoordinatesFromLocationStringUrl,
            AjaxFormattedLocationStringFromCurrentCoordinatesUrl: routes.FormattedLocationStringFromCurrentCoordinates,
            CurrentAuthenticatedUserLocationForCoordinates: currentUser.CurrentLocation?.CurrentAuthenticatedUserLocationForCoordinates,
            onSortChange: onSortChange,
            DistanceSortIsSelected: searchData.SelectedSortValue == 6,
            distanceSortID: 6,
            defaultSortID: 1,
            CurrentUserFormattedAddress: searchData.LocationString,
            ToggleDistanceModal: onDistanceLocationModalToggle,
            ShowDistanceSortError: distanceSortError
        };
    };


    const renderError = () => {
        return (
            <div className='list-error-container'>
                <div className='info'>
                    <h2 className="no-listings-found">
                        <i className="fas fa-info-circle"></i>&nbsp;&nbsp;{props.IsEvents ? <TranslationText tKey='sandhills.noresultsfound' /> : <TranslationText tKey='sandhills.tradesites.mvc.noauctioneersfound' />}
                        </h2>
                        {(criteriaData.SelectedFacets && criteriaData.SelectedFacets.length > 0) &&
                            <>
                                <div>
                                    <button className='ts-button' onClick={clearAll}>{translator.translate('sandhills.startanewsearch').toString().toUpperCase()}</button>
                                </div>
                                <div className="or-line">
                                    <TranslationText tKey='sandhills.tradepubwebsite.mvc.or' />
                                </div>
                                <div>
                                    <span>{translator.translate('sandhills.adjustfiltersbyremovingthefollowing').toString()}:</span>
                        </div>
                        <div className="selected-facets">
                            {criteriaData.SelectedFacets.map(field =>
                                <FacetedSearchSelectedFacet
                                    {...field}
                                    key={'FacetedSearchSelectedFacet' + field.FieldName + '_EmptySearch'}
                                    ClearFacet={clearFacetDebounced}
                                    SearchURL={criteriaData.SearchBaseURL}
                                    IsNoListingsFoundSection
                                />)}
                        </div>
                            </>
                        }
                    </div>
            </div>
        );
    };

    return (
        <div className="list-content" id="toplevellist">
            {showDistanceModal &&
                <DistanceLocationModal
                    OnUpdate={onDistanceLocationModalUpdate}
                    ToggleDistanceModal={onDistanceLocationModalToggle}
                    LocationAutosuggestUrl={routes.LocationAutosuggestUrl}
                    AjaxGetCoordinatesFromLocationStringUrl={routes.AjaxGetCoordinatesFromLocationStringUrl}
                    AjaxFormattedLocationStringFromCurrentCoordinatesUrl={routes.FormattedLocationStringFromCurrentCoordinates}
                    CurrentAuthenticatedUserLocationForCoordinates={currentUser.CurrentLocation?.CurrentAuthenticatedUserLocationForCoordinates}
                    CurrentUserFormattedAddress={searchData.LocationString}
                    DisplayShowClosestFirstByConsent={props.DisplayShowClosestFirstByConsent}
                />}
            <section className='list-top-section'>
                <SimpleHomeBreadcrumb homeRoute={props.BreadCrumbHomeRoute} displayText={searchData.PageHeader} />
                <div className='list-bcrumbs-and-title'>
                    <div className='listing-header'>
                        <div className="listing-title">
                            <h1>{searchData.PageHeader} </h1>
                            <span className="listings-count">{searchData.ListingCountHeader}<TranslationText tKey='sandhills.tradesites.mvc.matches' /> </span>
                        </div>
                        <div className='media-buttons'>
                            {props.ShowPrintThis &&
                                <button id='list-page-print-this' type='button' className='print-this des-print-this ts-button' onClick={onPrint} target='_blank' disabled={loadImagesForPrint} aria-label={translator.translate('sandhills.print') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>
                                    <i className='fas fa-print media-buttons__icon' /> <TranslationText tKey='sandhills.print' />
                                </button>}
                            {props.ShowEmailThis &&
                                <button id='share' type='button' className='email-this des-email-this ts-button' ef={ref} onClick={() => openEmailModal(inquiryType.EmailThis)}>
                                    <i className='fas fa-share-alt media-buttons__icon' /> <TranslationText key='email-button' tKey='sandhillssystem.share' />
                                </button>}
                        </div>
                    </div>
                </div>
                <div className='listing-option-bar-div'>
                    <div className='listing-option-bar' id="auctioneer-bar">
                        {props.SortOrderItems && props.SortOrderItems.length > 0 &&
                            <div className='listing-option-bar-content' >
                                {props.SortOrderItems && props.SortOrderItems.length > 0 &&
                                    <SortDropdown
                                        ShowMobileSort={false}
                                        IsDesktop={!mobileSearch}
                                        SortOptions={props.SortOrderItems}
                                        OnChange={onSortChange}
                                        SelectedValue={searchData.SelectedSortValue}
                                        setShowDistanceModal={setShowDistanceModal}
                                    />}
                            </div>}
                        {searchData.SelectedSortValue == 6 &&
                            <div className='distance-sort-descriptor' >
                                <p className='distance-from'>
                                    <TranslationText tKey='sandhills.distancefromterm' replaceValues={['']} />
                                    <span className='distance-nearest-sort'>{searchData.LocationString}</span>
                                    <a className='distance-sort-update' role="button" tabindex="0" onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault(); 
                                            onDistanceLocationModalToggle();
                                        }
                                    }} onClick={onDistanceLocationModalToggle} aria-label="Update Location">
                                        <TranslationText tKey='sandhills.update' />
                                    </a>
                                </p>
                            </div>}
                    </div>
                </div>
            </section>
            <section className='bottom-section'>
                <div className='side-container'>
                    <FacetedSearch
                        {...searchData}
                        runSearch={runSearch}
                        clearAll={clearAll}
                        clearFacet={clearFacetDebounced}
                        disabled={searchInProgress}
                        showMobileFilters={showSearchWindow}
                        toggleMobileFilter={toggleSearchWindow}
                        mobileSearch={mobileSearch}
                        UseSEOHTagReorganization={props.UseSEOHTagReorganization}
                    />
                </div>
                <div id="main-content" className="list-main-section has-search-bar ">
                    {<ShowClosestFirstToggle {...buildDistanceSortProps()}
                        IsListingsPage={false}
                        DisplayShowClosestFirstByConsent={searchData.DisplayShowClosestFirstByConsent}
                    />}
                    <div className="list-container" id="listContainer">
                        <div className="listing-list">
                            {searchData.Auctioneers && searchData.Auctioneers.length > 0 ? searchData.Auctioneers.map((item) =>
                                <AuctioneerDirectoryListing
                                    {...item}
                                    LocationString={searchData.LocationString}
                                />) :
                                <div className='list-error'>
                                    {renderError()}
                                </div>                                }
                        </div>
                    </div>
                    {searchData.TotalPages > 1 &&
                        <Paging
                            currentPage={searchData.CurrentPage}
                            totalPages={searchData.TotalPages}
                            onChange={handlePageNumberChange}
                        />}
                </div>
                {showEmailModal &&
                    <EmailModal
                        {...samModel}
                        toggleEmailModal={onEmailModalToggle}
                        selectedInquiry={selectedInquiry}
                        selectedContactType={selectedContactType}
                        DefaultCultureCode={props.DefaultCultureCode}
                        ShouldPushCriteoEvent={props.ShowEmailThis}
                        ShowSocialShareButtons={showShareButtons}
                    />}
                <div className='clear' />
                <div>
                    {searchData.InternalLinkingModel && searchData.InternalLinkingModel.InternalLinkGroups &&
                        <SEOLinks
                            {...searchData.InternalLinkingModel}
                            SearchURL={auctioneerDirectorySearchURL}
                        />}
                </div>
            </section>
            <FacetedSearchMobileMenu
                filter={{
                    render: true,
                    toggle: toggleSearchWindow
                }}
                sort={{
                    render: showMobileSort,
                    toggle: toggleMobileSort,
                    change: onSortChange,
                    options: props.SortOrderItems,
                    value: searchData.SelectedSortValue
                }}
                flyout={{
                    render: props.UseClassicViewFlyout,
                    url: props.ClassicFlyoutModel.ClassicViewURL,
                    img: props.ClassicFlyoutModel.ClassicViewImageURL
                }}
                setShowDistanceModal={setShowDistanceModal}
            />
            {searchInProgress && <div className='searching-background' />}
        </div>
    );
};

export default AuctioneerListView;